import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { Type } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { PrimaryLookupService } from "./PrimaryLookupService";
import { BaseBlotterDataAdapter } from "../../mediator/adapter/BaseBlotterDataAdapter";
import { BaseLookupService } from "./BaseLookupService";
import { CubiqWorker } from "../../worker/CubiqWorker";
import { ServiceMessageBrokerFactory, PRIMITIVE } from "shared/provider-webworker";
import { IPromiseBlotterLookupService } from "../IPromiseBlotterLookupService";
import { PromiseBlotterLookupService } from "./PromiseBlotterLookupService";
import { Subject } from "rxjs";
import { ModelDeleteEvent } from "../../model/core/ModelDeleteEvent";
import { BFXNotificationEventType } from "../../model/core/notifications/BFXNotificationEventType";
import { IOrderEntryEvent } from "../../model/core/IOrderEntryEvent";
import { INotificationService } from "shared/cubiq-services/lookup/notification/INotificationService";

export enum ExtendedOrderEntryLogType {
  NONE,
  FORWARD_IGNORE_SIMPLE,
  FORWARD_IGNORE_EXTENDED,
  LISTENER_ADD,
  LISTENER_REMOVE,
  TEXT_ONLY,
  TEXT_REF,
  FWD_UPDATE,
}

export abstract class PrimaryBlotterLookupService<K, V extends CubiqTS.ConcreteEncodable>
  extends PrimaryLookupService<K, V>
  implements PromiseBlotterLookupService<K, V>, IPromiseBlotterLookupService<K, V>
{
  protected primed: boolean = false;
  protected adaptedDataNotificationSubject: Subject<any> = new Subject<any>();
  // protected notifTypes: Set<BFXNotificationType>;
  /**
   * detailFillIds map contains key:orderId value:orderRef
   * orderRef is stored for unlock notifications.
   */
  protected detailFillIds: Map<string, string>;
  // protected appNotifTypes: Set<BFXAppNotificationType>;
  protected modelDeleteEventPorts: Array<MessagePort> = new Array<MessagePort>();
  protected modelDeleteEventSubject: Subject<any> = new Subject<any>();
  protected modelDeleteWithReasonEventSubject: Subject<any> = new Subject<any>();
  // protected modelNotificationEventSubject: Subject<INotificationEvent> = new Subject<INotificationEvent>();
  // protected modelAppNotificationEventSubject: Subject<any> = new Subject<any>();
  protected modelErrorEventSubject: Subject<any> = new Subject<any>();
  protected modelBatchUpdateSubject: Subject<any> = new Subject<any>();
  protected modelOrderEntryEventSubject: Subject<IOrderEntryEvent> = new Subject();
  protected orderEntryClearEventSubject: Subject<IOrderEntryEvent> = new Subject();
  protected modelDetailFillEventSubject: Subject<any> = new Subject<any>();
  protected orderEntryExtendedLogging: boolean = true; // used to activate logger output for order entry

  constructor(
    base: BaseLookupService<K, V>,
    factory: ServiceMessageBrokerFactory,
    lookupType: Type<V>,
    cubiqWorker: CubiqWorker,
    serviceChannel: string,
    modelUpdateEventType: Type<CubiqTS.ConcreteEncodable> = CubiqTS.ModelUpdateImpl,
    _: BaseBlotterDataAdapter,
    protected notifService: INotificationService,
  ) {
    super(base, factory, lookupType, cubiqWorker, serviceChannel, modelUpdateEventType);

    logger.out.info(`BlotterLookupService - Creating instance of ${this.constructor.name}`);

    // this.broker.registerMethod('setNotificationTypes', [PRIMITIVE], this.setNotificationTypes.bind(this), null);
    // this.broker.registerMethod('setAppNotificationTypes', [PRIMITIVE], this.setAppNotificationTypes.bind(this), null);
    this.broker.registerMethod(
      "setDetailFillIds",
      [PRIMITIVE],
      this.setDetailFillIds.bind(this),
      null,
    );
    this.broker.registerMethod(
      "setExtendedLogging",
      [PRIMITIVE],
      this.setExtendedLogging.bind(this),
      null,
    );
  }

  protected keyProperty(): string {
    return "orderId";
  }

  public setDetailFillIds(orderIdRefs: Array<[string, string]>): void {
    if (orderIdRefs && orderIdRefs.length > 0) {
      this.detailFillIds = new Map(orderIdRefs);
    } else {
      this.detailFillIds = null;
    }
  }

  // public setAppNotificationTypes(types: Array<BFXAppNotificationType>): void {
  //     if(types&&types.length>0){
  //         this.appNotifTypes = new Set(types);
  //     } else {
  //         this.appNotifTypes = null;
  //     }
  // }

  getModelDeleteSubject(): Subject<K> {
    return this.modelDeleteEventSubject;
  }

  getModelDeleteWithReasonSubject(): Subject<K> {
    return this.modelDeleteWithReasonEventSubject;
  }

  getModelErrorSubject(): Subject<any> {
    return this.modelErrorEventSubject;
  }

  getModelOrderEntrySubject(): Subject<IOrderEntryEvent> {
    return this.modelOrderEntryEventSubject;
  }

  getOrderEntryClearEventSubject(): Subject<IOrderEntryEvent> {
    return this.orderEntryClearEventSubject;
  }

  getModelDetailFillSubject(): Subject<any> {
    return this.modelDetailFillEventSubject;
  }

  // getModelNotificationSubject(): Subject<INotificationEvent> {
  //     return this.modelNotificationEventSubject;
  // }

  // getModelAppNotificationSubject(): Subject<CubiqTS.ConcreteEncodable> {
  //     return this.modelAppNotificationEventSubject;
  // }

  getModelBatchUpdateSubject(): Subject<any> {
    return this.modelBatchUpdateSubject;
  }

  startUpdates(): void {
    throw new Error("Unsupported call: startUpdates");
  }

  protected fireModelUpdateEvent(
    rawData: V,
    additionalData: any = null,
    dispatchUpdate: boolean = true,
  ): any {
    if (rawData != null) {
      return this.fireAdaptedDataUpdateEvent(rawData, additionalData, dispatchUpdate);
    }
    return null;
  }

  protected fireAdaptedDataUpdateEvent(
    data: V,
    additionalData: any = null,
    dispatchUpdate: boolean = true,
  ): any {
    if (data != null && additionalData != null) {
      data = Object.assign(Object.create(data), data, additionalData);
    }
    if (data != null && dispatchUpdate) {
      this.modelUpdateEventSubject.next(data);

      //   this.modelUpdateEventPorts.forEach((port: MessagePort) => {
      //       try {
      //           port.postMessage({ modelEventType: LookupEventType.UPDATE, payload: [data] });
      //       } catch (ex) {
      //           logger.out.error(ex);
      //           logger.out.error(ex.stack);
      //       }
      //   });
    }
    return data;
  }

  // order events that are not covered by model update events
  // protected fireModelNotificationEvent(notifEvent: INotificationEvent): void {
  //     if (notifEvent != null) {
  //         this.modelNotificationEventSubject.next(notifEvent);
  //     }
  // }

  protected fireModelDetailFillClearEvent(
    id: K,
    lockUser?: string,
    orderRef?: string,
    dispatchUpdate: boolean = true,
  ): any {
    if (id != null) {
      let payload = { id: id.toString(), type: "DetailFillClear" };
      if (lockUser != undefined && lockUser != null) payload["lockUser"] = lockUser;
      if (orderRef != undefined && orderRef != null) payload["orderRef"] = orderRef;
      if (dispatchUpdate) {
        this.modelDetailFillEventSubject.next(payload);
        // this.modelUpdateEventPorts.forEach((port: MessagePort) => {
        //     try {
        //         port.postMessage({modelEventType: LookupEventType.DETAIL_FILL, payload:payload});
        //     } catch (ex) {
        //         logger.out.error(ex);
        //     }
        // });
      }
      return payload;
    }
  }

  protected fireModelNotificationClearEvent(
    ids: Array<K>,
    clearAcceptanceOnly: boolean = false,
  ): void {
    this.notifService.fireNotificationClearEvent(
      this.notifService.dataToClearEvent(ids, clearAcceptanceOnly),
      BFXNotificationEventType.ORDER_UPDATE_EVENT,
    );

    // if (ids != null) {
    // this.notifService.fireNotificationClearEvent(this.notifService.dataToClearEvent(ids), BFXNotificationEventType.);
    //     let payload = {ids:ids,notifType:BFXNotificationType.CLEAR_NOTIFICATION}
    //     this.modelNotificationEventSubject.next(payload);
    //     this.modelUpdateEventPorts.forEach((port: MessagePort) => {
    //         try {
    //             port.postMessage({modelEventType: LookupEventType.NOTIFICATION, payload:payload});
    //         } catch (ex) {
    //             logger.out.error(ex);
    //         }
    //     });
    // }
  }

  public adaptLoginNotifications(rawData: V, additionalData: any = null): any {
    // if (rawData != null) {
    //     let data: any = this.rawDataAdapter.adaptRow(rawData);

    //     if (data != null && additionalData != null) {
    //         Object.assign(data, additionalData);
    //     }
    //     return data;
    // }
    return null;
  }

  public fireAppModelNotificationEvent(rawData: V, additionalData: any = null): void {
    // if (rawData != null) {
    //     let data: any = this.rawDataAdapter.adaptRow(rawData);
    //     if (data != null && additionalData != null) {
    //         Object.assign(data, additionalData);
    //     }
    //     this.modelAppNotificationEventSubject.next(data);
    //     // this.modelUpdateEventSubject.next(rawData);
    //     this.modelUpdateEventPorts.forEach((port: MessagePort) => {
    //         try {
    //             port.postMessage({ modelEventType: LookupEventType.NOTIFICATION, payload: data });
    //         } catch (ex) {
    //             logger.out.error(ex);
    //         }
    //     });
    // }
  }

  protected fireModelDeleteEvent(id: K, dispatchUpdate: boolean = true): void {
    if (id != null) {
      this.base.deleteItemById(id);
      if (dispatchUpdate) {
        this.modelDeleteEventSubject.next(id);
        // this.modelUpdateEventPorts.forEach((port: MessagePort) => {
        //     try {
        //         port.postMessage({ modelEventType: LookupEventType.DELETE, payload: id });
        //     } catch (ex) {
        //         logger.out.error(ex);
        //     }
        // });
      }
    }
  }

  protected fireModelDeleteWithReasonEvent(
    id: K,
    reason?: string,
    data?: any,
    additionalData?: any,
    dispatchUpdate: boolean = true,
  ): void {
    if (id != null) {
      this.base.deleteItemById(id);
      if (dispatchUpdate) {
        let delEv: ModelDeleteEvent = new ModelDeleteEvent(id);
        if (reason && reason != null) delEv.reason = reason;
        // if (data && data != null) {
        // delEv.adaptData = this.adaptItem(data);
        // }
        if (additionalData) {
          Object.assign(delEv, additionalData);
        }
        this.modelDeleteWithReasonEventSubject.next(delEv);
        // this.modelUpdateEventPorts.forEach((port: MessagePort) => {
        //     try {
        //         port.postMessage({ modelEventType: LookupEventType.DELETE_WITH_REASON, payload: delEv });
        //     } catch (ex) {
        //         logger.out.error(ex);
        //     }
        // });
      }
    }
  }

  protected fireModelOrderEntryEvent(metaData: IOrderEntryEvent, additionalData: any = null): any {
    if (metaData.ref != null) {
      let data: any = metaData;

      // if (data != null && additionalData != null) {
      //     if (additionalData instanceof CubiqTS.InterventionEventImpl || additionalData instanceof CubiqTS.OrderDataImpl) {
      //         Object.assign(data, this.rawDataAdapter.adaptRow(additionalData));
      //     } else {
      //         Object.assign(data, additionalData);
      //     }
      // }

      this.logOrderEntryDebugInfo(data, null, ExtendedOrderEntryLogType.FWD_UPDATE);

      this.modelOrderEntryEventSubject.next(data);
      // this.modelUpdateEventPorts.forEach((port: MessagePort) => {
      //     try {
      //         port.postMessage({ modelEventType: LookupEventType.ORDER_ENTRY, payload: data });
      //     } catch (ex) {
      //         logger.out.error(ex);
      //     }
      // });

      return data;
    } else {
      this.logOrderEntryDebugInfo(
        null,
        `FWD => UPDATE === FAIL`,
        ExtendedOrderEntryLogType.TEXT_ONLY,
      );
    }
  }

  protected fireModelErrorEvent(event: CubiqTS.ConcreteEncodable, ...vargs): void {
    this.modelErrorEventSubject.next(event);
  }

  protected fireOrderEntryClearEvent(data: IOrderEntryEvent): void {
    if (data.ref != null) {
      this.logOrderEntryDebugInfo(
        data.ref,
        "FWD => CLEAR Event",
        ExtendedOrderEntryLogType.TEXT_REF,
      );
      this.orderEntryClearEventSubject.next(data);
      // this.modelUpdateEventPorts.forEach((port: MessagePort) => {
      //     try {
      //         port.postMessage({ modelEventType: LookupEventType.ORDER_ENTRY_CLEAR, payload: data });
      //     } catch (ex) {
      //         logger.out.error(ex);
      //     }
      // });
    } else {
      this.logOrderEntryDebugInfo(
        null,
        `FWD => CLEAR === FAIL`,
        ExtendedOrderEntryLogType.TEXT_ONLY,
      );
    }
  }

  protected logOtherDebugInfo(
    message: string,
    customMessage: string = "",
    feature: string = "OTHER",
  ): void {
    try {
      if (this.orderEntryExtendedLogging) {
        let prefix = `WORKER: Cubiq-services@${Date.now()}: ${feature}:`;
        logger.out.info(
          `${prefix} ${typeof message === "string" ? message : ""} ${
            typeof customMessage === "string" ? customMessage : ""
          }`,
        );
      }
    } catch (ex) {
      logger.out.error(`WORKER-ERROR: Extended logging exception (Other): %O`, ex);
    }
  }

  protected logOrderEntryDebugInfo(
    eventData: any,
    customMessage: string = "",
    type: ExtendedOrderEntryLogType = ExtendedOrderEntryLogType.NONE,
    additionalData: any = null,
  ): void {
    try {
      if (
        this.orderEntryExtendedLogging &&
        (eventData != null ||
          (type === ExtendedOrderEntryLogType.TEXT_ONLY &&
            typeof customMessage === "string" &&
            customMessage.length > 1))
      ) {
        let prefix = `WORKER: Cubiq-services@${Date.now()}: OE:`;
        let prefixSP = `WORKER: Cubiq-services@${Date.now()}: SCRATCH-PAD:`;
        if (type != ExtendedOrderEntryLogType.NONE) {
          let typeText =
            this.serviceChannel === "InterventionEventImpl"
              ? "INTERVENTION"
              : eventData.lockEvent
              ? "UPDATE-LOCK"
              : "UPDATE-ORDER";
          let referenceId =
            eventData.ref != null
              ? eventData.ref
              : this.serviceChannel === "InterventionEventImpl"
              ? eventData.orderData != null
                ? eventData.orderData.clOrdID
                : "N/A"
              : eventData.lockEvent
              ? eventData.clOrderIds.toString()
              : eventData.externalClientReference;
          switch (type) {
            case ExtendedOrderEntryLogType.FORWARD_IGNORE_SIMPLE:
              logger.out.info(
                `${prefix} IGNORE >>> ${typeText} (msgID: ${eventData.messageId}) => [${referenceId}] -> ${customMessage}`,
              );
              break;
            case ExtendedOrderEntryLogType.FORWARD_IGNORE_EXTENDED:
              let creditUpdate =
                eventData.eventTypeId === CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE ? "YES" : "NO";
              let rollBack = eventData.rollbackEventFg ? "YES" : "NO";
              let noData = additionalData == null ? "YES" : "NO";
              logger.out.info(
                `${prefix} IGNORE >>> ${typeText} (msgID: ${eventData.messageId}) => [${referenceId}] -> creditUpdate: ${creditUpdate} | rollback: ${rollBack} | noData: ${noData}`,
              );
              break;
            case ExtendedOrderEntryLogType.LISTENER_ADD:
              logger.out.info(`${prefix} ${customMessage} Listener ADDED [${eventData}]`);
              break;
            case ExtendedOrderEntryLogType.LISTENER_REMOVE:
              logger.out.info(`${prefix} ${customMessage} Listener REMOVED [${eventData}]`);
              break;
            case ExtendedOrderEntryLogType.TEXT_ONLY:
              logger.out.info(
                `${prefix} ${typeof eventData === "string" ? eventData : ""} ${
                  typeof customMessage === "string" ? customMessage : ""
                }`,
              );
              break;
            case ExtendedOrderEntryLogType.TEXT_REF:
              logger.out.info(`${prefix} ${customMessage} [${eventData}]`);
              break;
            case ExtendedOrderEntryLogType.FWD_UPDATE:
              let logBody: string = null;
              if (this.serviceChannel === "InterventionEventImpl") {
                let logInvEvtTypeNum = eventData.invEvtType != null ? eventData.invEvtType : "N/A";
                let logInvEvtTypeEnum =
                  eventData.invEvtType != null
                    ? CubiqTS.InterventionEventType[eventData.invEvtType]
                    : "N/A";
                let logInvOprTypeNum = eventData.invOprType != null ? eventData.invOprType : "N/A";
                let logInvOprTypeEnum =
                  eventData.invOprType != null
                    ? CubiqTS.InterventionOperationType[eventData.invOprType]
                    : "N/A";
                let errorsText = `errors ${
                  eventData.errors != null &&
                  eventData.errors.errorMessages != null &&
                  eventData.errors.errorMessages instanceof Array &&
                  eventData.errors.errorMessages.length > 0
                    ? "YES"
                    : "NO"
                }`;
                logBody = `| inv.EVT: (${logInvEvtTypeNum}) = [ ${logInvEvtTypeEnum} ] | inv.OPR: (${logInvOprTypeNum}) = [ ${logInvOprTypeEnum} ] | ${errorsText} ${
                  errorsText === "YES" ? eventData.errors.errorMessages.toString() : ""
                }`;
              } else {
                let stateText = `state: (${
                  eventData.state != null ? eventData.state : "N/A"
                }) = [ ${eventData.state != null ? CubiqTS.OrderState[eventData.state] : "N/A"} ]`;
                let eventTypeText = `eventType: (${
                  eventData.evtType != null ? eventData.evtType : "N/A"
                }) = [ ${CubiqTS.FxLifeCycleEventType[eventData.evtType]}]`;
                let errorsText = `errors ${
                  eventData.errors != null &&
                  eventData.errors.errorMessages != null &&
                  eventData.errors.errorMessages instanceof Array &&
                  eventData.errors.errorMessages.length > 0
                    ? "YES"
                    : "NO"
                }`;
                logBody = `|  ${stateText} | ${eventTypeText} | ${errorsText}  ${
                  errorsText === "YES" ? eventData.errors.errorMessages.toString() : ""
                }`;
              }
              logger.out.info(
                `${prefix} FWD >>> ${typeText} Event (msgID: ${eventData.messageId}) => [${eventData.ref}] ${logBody} `,
              );
              break;
          }
        } else if (eventData instanceof CubiqTS.OrderLifeCycleEventImpl) {
          let logType = `type: ${
            eventData.eventTypeId != null
              ? CubiqTS.FxLifeCycleEventType[eventData.eventTypeId]
              : "N/A"
          }`;
          let logState = `state: ${
            eventData.orderData != null && eventData.orderData.orderState != null
              ? "(" +
                eventData.orderData.orderState +
                ") = [" +
                CubiqTS.OrderState[eventData.orderData.orderState] +
                "]"
              : "N/A"
          } `;
          logger.out.info(
            `${prefix} UPDATE-ORDER Event (msgID: ${eventData.messageId}) ${customMessage} => [${
              eventData.externalClientReference
            }]  (ID: ${eventData.orderId}) | ${logType} | ${logState} | orderData: ${
              eventData.orderData != null ? "ATTACHED" : "EMPTY"
            }`,
          );
        } else if (eventData instanceof CubiqTS.LockLifeCycleEventImpl) {
          let logType = `type: ${
            eventData.eventTypeId != null
              ? CubiqTS.FxLifeCycleEventType[eventData.eventTypeId]
              : "N/A"
          }`;
          let logLockType = ` lockType: ${
            eventData.lockType != null ? CubiqTS.LockType[eventData.lockType] : "N/A"
          }`;
          logger.out.info(
            `${prefix} UPDATE-LOCK Event (msgID: ${
              eventData.messageId
            }) ${customMessage} => [${eventData.clOrderIds.toString()}] (ID: ${eventData.orderIds.toString()}) | ${logType} | ${logLockType}`,
          );
        } else if (eventData instanceof CubiqTS.PauseLifeCycleEventImpl) {
          let logType = ` type: ${
            eventData.eventTypeId != null
              ? CubiqTS.FxLifeCycleEventType[eventData.eventTypeId]
              : "N/A"
          } `;
          logger.out.info(
            `${prefix} UPDATE-PAUSE Event (msgID: ${
              eventData.messageId
            }) ${customMessage} =>  (Data: ${eventData.orders.toString()}) | ${logType} `,
          );
        } else if (eventData instanceof CubiqTS.BulkOrderLifeCycleEventImpl) {
          logger.out.info(
            `${prefix} BULK-ORDER Event (msgID: ${eventData.messageId}) ${customMessage} => (${
              eventData.events.length
            }) type: ${
              eventData.eventTypeId != null
                ? CubiqTS.FxLifeCycleEventType[eventData.eventTypeId]
                : "NULL"
            }`,
          );
        } else if (eventData instanceof CubiqTS.InterventionEventImpl) {
          let logInvEvtTypeNum = eventData.eventType != null ? eventData.eventType : "N/A";
          let logInvEvtTypeEnum =
            eventData.eventType != null
              ? CubiqTS.InterventionEventType[eventData.eventType]
              : "N/A";
          let logInvOprTypeNum = eventData.operationType != null ? eventData.operationType : "N/A";
          let logInvOprTypeEnum =
            eventData.operationType != null
              ? CubiqTS.InterventionOperationType[eventData.operationType]
              : "N/A";
          logger.out.info(
            `${prefix} INTERVENTION Event (msgID: ${eventData.messageId}) => [${
              eventData.orderData != null ? eventData.orderData.clOrdID : "N/A"
            }] | Inv.EVT: (${logInvEvtTypeNum}) = [ ${logInvEvtTypeEnum} ] | Inv.OPR: (${logInvOprTypeNum}) = [ ${logInvOprTypeEnum} ]`,
          );
          // scratchpad specific events
        } else if (eventData instanceof CubiqTS.SavedOrderEventImpl) {
          let logRef =
            eventData.orderDataRequest != null && eventData.orderDataRequest.rootOrder != null
              ? eventData.orderDataRequest.rootOrder.externalClientReference
              : "N/A";
          //let logState = `state: ${eventData.orderDataRequest != null && eventData.orderDataRequest.rootOrder != null ? '('+ (eventData.orderDataRequest.rootOrder as any).orderState + ') = [' +CubiqTS.OrderState[ (eventData.orderDataRequest.rootOrder as any).orderState] +']': 'N/A' } `;
          logger.out.info(
            `${prefixSP} SAVED-ORDER Event (msgID: ${eventData.messageId}) => [${logRef}] `,
          );
        } else if (eventData instanceof CubiqTS.SavedOrderEventListImpl) {
          logger.out.info(`${prefixSP} SAVED-ORDER-LIST Event (msgID: ${eventData.messageId})`);
        } else {
          logger.out.info(
            `${prefix} Incorrect debug log entry! type: ${ExtendedOrderEntryLogType[type]} %O`,
            eventData,
          );
        }
      }
    } catch (ex) {
      logger.out.error(`WORKER-ERROR: Extended logging exception (Order Entry): %O`, ex);
    }
  }

  public setExtendedLogging(logState: boolean): void {
    this.orderEntryExtendedLogging = logState;
  }
}
